import React, { useId, useMemo } from 'react';
import styles from './Dashboard.module.scss';

/**
 * Full circular ring (reference): rotate -90° so progress starts at 12 o'clock,
 * strokeDashoffset = circumference * (1 - score/100).
 */
export function HealthGauge({ score = 0, badgeText = null }) {
  const uid = useId().replace(/:/g, '');
  const gradId = `gaugeGradient-${uid}`;

  const s = Math.max(0, Math.min(100, Number(score) || 0));

  const { radius, cx, cy, circumference, offset } = useMemo(() => {
    const r = 70;
    const c = 96;
    const circ = 2 * Math.PI * r;
    const off = circ - (s / 100) * circ;
    return { radius: r, cx: c, cy: c, circumference: circ, offset: off };
  }, [s]);

  return (
    <div className={styles.healthGauge} aria-label={`System health ${Math.round(s)} percent`}>
      <svg viewBox="0 0 192 192" className={styles.healthGaugeSvg} aria-hidden>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#E96A5A" />
            <stop offset="50%" stopColor="#F2B94B" />
            <stop offset="100%" stopColor="#3DBE7A" />
          </linearGradient>
        </defs>

        {/* Background ring — full circle */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="transparent"
          stroke="currentColor"
          strokeWidth="12"
          className={styles.healthGaugeTrack}
        />

        {/* Progress ring — length follows score % */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="transparent"
          stroke={`url(#${gradId})`}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={styles.healthGaugeProgress}
        />
      </svg>

      <div className={styles.healthGaugeCenter}>
        <span className={styles.healthGaugeValue}>
          {Math.round(s)}
          <span className={styles.healthGaugePctSuffix} aria-hidden>
            %
          </span>
        </span>
        <span className={styles.healthGaugeLabel}>System Health</span>
        {badgeText ? (
          <div className={styles.healthGaugeBadge}>
            <svg
              className={styles.healthGaugeBadgeIcon}
              width="8"
              height="8"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
            {badgeText}
          </div>
        ) : null}
      </div>
    </div>
  );
}
